import { useState, useEffect } from 'react';
import { useAuth } from '../../../context/AuthContext';
import { 
  Split, Users, Calculator, CreditCard, Banknote, Check, 
  Plus, Minus, Trash2, MoveRight, Loader2, X, ArrowLeftRight
} from 'lucide-react';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import { Badge } from '../../../components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '../../../components/ui/dialog';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '../../../components/ui/tabs';
import { toast } from 'sonner';

export default function BillSplitter({ 
  open, 
  onClose, 
  order, 
  restaurantId,
  onComplete 
}) {
  const { api } = useAuth();
  const [splitMode, setSplitMode] = useState('equal'); // equal, items, amount
  const [splits, setSplits] = useState([]);
  const [numberOfPeople, setNumberOfPeople] = useState(2);
  const [processing, setProcessing] = useState(false);
  const [payingSplitIndex, setPayingSplitIndex] = useState(null);
  const [paymentMethod, setPaymentMethod] = useState('card');
  const [cashReceived, setCashReceived] = useState('');

  // Initialize splits when dialog opens or mode changes
  useEffect(() => {
    if (open && order) {
      initializeSplits();
    }
  }, [open, order, splitMode, numberOfPeople]);

  const initializeSplits = () => {
    if (splitMode === 'equal') {
      // Split total evenly
      const amountPerPerson = order.total / numberOfPeople;
      const newSplits = Array(numberOfPeople).fill(null).map((_, i) => ({
        id: `split-${i}`,
        label: `Guest ${i + 1}`,
        amount: amountPerPerson,
        items: [], // All items shared
        isPaid: false,
        paymentMethod: null
      }));
      setSplits(newSplits);
    } else if (splitMode === 'items') {
      // Each person gets their own items (start with 2 empty splits)
      const newSplits = Array(numberOfPeople).fill(null).map((_, i) => ({
        id: `split-${i}`,
        label: `Guest ${i + 1}`,
        amount: 0,
        items: [],
        isPaid: false,
        paymentMethod: null
      }));
      setSplits(newSplits);
    } else if (splitMode === 'amount') {
      // Custom amounts
      setSplits([
        {
          id: 'split-0',
          label: 'Guest 1',
          amount: order.total,
          items: [],
          isPaid: false,
          paymentMethod: null
        }
      ]);
    }
  };

  // Calculate remaining amount (total - paid amounts)
  const paidAmount = splits.filter(s => s.isPaid).reduce((sum, s) => sum + s.amount, 0);
  const remainingAmount = order ? order.total - paidAmount : 0;

  // Add a new split
  const addSplit = () => {
    setSplits(prev => [...prev, {
      id: `split-${prev.length}`,
      label: `Guest ${prev.length + 1}`,
      amount: 0,
      items: [],
      isPaid: false,
      paymentMethod: null
    }]);
  };

  // Remove a split
  const removeSplit = (index) => {
    if (splits.length <= 1) {
      toast.error('Must have at least one split');
      return;
    }
    if (splits[index].isPaid) {
      toast.error('Cannot remove a paid split');
      return;
    }
    setSplits(prev => prev.filter((_, i) => i !== index));
  };

  // Update split amount
  const updateSplitAmount = (index, amount) => {
    setSplits(prev => {
      const newSplits = [...prev];
      newSplits[index] = { ...newSplits[index], amount: parseFloat(amount) || 0 };
      return newSplits;
    });
  };

  // Update split label
  const updateSplitLabel = (index, label) => {
    setSplits(prev => {
      const newSplits = [...prev];
      newSplits[index] = { ...newSplits[index], label };
      return newSplits;
    });
  };

  // Assign item to split (for item-based splitting)
  const assignItemToSplit = (itemIndex, splitIndex) => {
    setSplits(prev => {
      const newSplits = prev.map(s => ({
        ...s,
        items: s.items.filter(i => i !== itemIndex)
      }));
      
      if (splitIndex >= 0) {
        newSplits[splitIndex].items.push(itemIndex);
        // Recalculate amount based on items
        const splitItems = newSplits[splitIndex].items;
        const splitTotal = splitItems.reduce((sum, idx) => {
          const item = order.items[idx];
          return sum + (item.price * item.quantity);
        }, 0);
        newSplits[splitIndex].amount = splitTotal;
      }
      
      return newSplits;
    });
  };

  // Get unassigned items
  const assignedItems = splits.flatMap(s => s.items);
  const unassignedItems = order?.items?.map((_, i) => i).filter(i => !assignedItems.includes(i)) || [];

  // Pay a split
  const paySplit = async (index) => {
    const split = splits[index];
    
    if (split.amount <= 0) {
      toast.error('Split amount must be greater than 0');
      return;
    }

    if (paymentMethod === 'cash') {
      const received = parseFloat(cashReceived) || 0;
      if (received < split.amount) {
        toast.error('Insufficient cash amount');
        return;
      }
    }

    setProcessing(true);
    try {
      // Update split as paid
      setSplits(prev => {
        const newSplits = [...prev];
        newSplits[index] = { 
          ...newSplits[index], 
          isPaid: true, 
          paymentMethod,
          cashReceived: paymentMethod === 'cash' ? parseFloat(cashReceived) : null,
          change: paymentMethod === 'cash' ? parseFloat(cashReceived) - split.amount : null
        };
        return newSplits;
      });

      // Save split payment to backend
      await api.post(`/orders/${order.id}/splits/${split.id}/pay`, {
        amount: split.amount,
        payment_method: paymentMethod,
        cash_received: paymentMethod === 'cash' ? parseFloat(cashReceived) : null
      });

      toast.success(`${split.label} paid £${split.amount.toFixed(2)}`);
      setPayingSplitIndex(null);
      setCashReceived('');

      // Check if all splits paid
      const allPaid = splits.every((s, i) => i === index ? true : s.isPaid);
      if (allPaid) {
        toast.success('Bill fully paid!');
        onComplete?.({ splits });
      }
    } catch (error) {
      console.error('Payment failed:', error);
      toast.error('Payment failed');
    } finally {
      setProcessing(false);
    }
  };

  // Split evenly
  const splitEvenly = () => {
    const amountPerPerson = remainingAmount / splits.filter(s => !s.isPaid).length;
    setSplits(prev => prev.map(s => 
      s.isPaid ? s : { ...s, amount: amountPerPerson }
    ));
  };

  if (!order) return null;

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Split className="w-5 h-5" />
            Split Bill
          </DialogTitle>
          <DialogDescription>
            Order Total: <span className="font-bold text-orange-500">£{order.total?.toFixed(2)}</span>
            {paidAmount > 0 && (
              <span className="ml-2 text-green-600">
                (£{paidAmount.toFixed(2)} paid, £{remainingAmount.toFixed(2)} remaining)
              </span>
            )}
          </DialogDescription>
        </DialogHeader>

        {/* Split Mode Tabs */}
        <Tabs value={splitMode} onValueChange={setSplitMode} className="w-full">
          <TabsList className="grid w-full grid-cols-3">
            <TabsTrigger value="equal" className="flex items-center gap-2" data-testid="split-equal-tab">
              <Users className="w-4 h-4" />
              Split Equally
            </TabsTrigger>
            <TabsTrigger value="items" className="flex items-center gap-2" data-testid="split-items-tab">
              <ArrowLeftRight className="w-4 h-4" />
              By Items
            </TabsTrigger>
            <TabsTrigger value="amount" className="flex items-center gap-2" data-testid="split-amount-tab">
              <Calculator className="w-4 h-4" />
              Custom Amount
            </TabsTrigger>
          </TabsList>

          {/* Equal Split */}
          <TabsContent value="equal" className="space-y-4">
            <div className="flex items-center gap-4">
              <Label>Number of People:</Label>
              <div className="flex items-center gap-2">
                <Button 
                  variant="outline" 
                  size="icon"
                  onClick={() => setNumberOfPeople(Math.max(2, numberOfPeople - 1))}
                  disabled={numberOfPeople <= 2}
                >
                  <Minus className="w-4 h-4" />
                </Button>
                <span className="w-8 text-center font-bold">{numberOfPeople}</span>
                <Button 
                  variant="outline" 
                  size="icon"
                  onClick={() => setNumberOfPeople(numberOfPeople + 1)}
                  disabled={numberOfPeople >= 10}
                >
                  <Plus className="w-4 h-4" />
                </Button>
              </div>
              <span className="text-sm text-slate-500">
                £{(order.total / numberOfPeople).toFixed(2)} each
              </span>
            </div>
          </TabsContent>

          {/* Items Split */}
          <TabsContent value="items" className="space-y-4">
            <div className="text-sm text-slate-500 mb-2">
              Drag items to assign them to each person
            </div>
            
            {/* Unassigned Items */}
            {unassignedItems.length > 0 && (
              <div className="bg-slate-100 rounded-lg p-3 mb-4">
                <Label className="text-sm text-slate-500 mb-2 block">Unassigned Items</Label>
                <div className="space-y-1">
                  {unassignedItems.map(idx => {
                    const item = order.items[idx];
                    return (
                      <div key={idx} className="flex items-center justify-between bg-white p-2 rounded">
                        <span>{item.quantity}x {item.name}</span>
                        <span>£{(item.price * item.quantity).toFixed(2)}</span>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </TabsContent>

          {/* Custom Amount Split */}
          <TabsContent value="amount" className="space-y-4">
            <Button variant="outline" size="sm" onClick={splitEvenly}>
              <Calculator className="w-4 h-4 mr-2" />
              Split Remaining Evenly
            </Button>
          </TabsContent>
        </Tabs>

        {/* Splits List */}
        <div className="space-y-3 mt-4">
          {splits.map((split, index) => (
            <div 
              key={split.id} 
              className={`border rounded-lg p-4 ${
                split.isPaid 
                  ? 'bg-green-50 border-green-300' 
                  : payingSplitIndex === index 
                    ? 'bg-blue-50 border-blue-300'
                    : 'bg-white border-slate-200'
              }`}
              data-testid={`split-${index}`}
            >
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <Input
                    value={split.label}
                    onChange={(e) => updateSplitLabel(index, e.target.value)}
                    className="w-32 h-8"
                    disabled={split.isPaid}
                  />
                  {split.isPaid && (
                    <Badge className="bg-green-500">
                      <Check className="w-3 h-3 mr-1" />
                      Paid via {split.paymentMethod}
                    </Badge>
                  )}
                </div>
                {!split.isPaid && splits.length > 1 && (
                  <Button 
                    variant="ghost" 
                    size="icon"
                    onClick={() => removeSplit(index)}
                    className="text-red-500 hover:text-red-600"
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                )}
              </div>

              {/* Items (for items mode) */}
              {splitMode === 'items' && split.items.length > 0 && (
                <div className="space-y-1 mb-3">
                  {split.items.map(idx => {
                    const item = order.items[idx];
                    return (
                      <div key={idx} className="flex items-center justify-between text-sm">
                        <span>{item.quantity}x {item.name}</span>
                        <span>£{(item.price * item.quantity).toFixed(2)}</span>
                      </div>
                    );
                  })}
                </div>
              )}

              {/* Amount */}
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Label>Amount:</Label>
                  {splitMode === 'amount' && !split.isPaid ? (
                    <Input
                      type="number"
                      step="0.01"
                      value={split.amount}
                      onChange={(e) => updateSplitAmount(index, e.target.value)}
                      className="w-24 h-8"
                    />
                  ) : (
                    <span className="font-bold text-lg">£{split.amount.toFixed(2)}</span>
                  )}
                </div>

                {!split.isPaid && (
                  <Button 
                    size="sm"
                    onClick={() => setPayingSplitIndex(index)}
                    className="bg-green-600 hover:bg-green-700"
                    data-testid={`pay-split-${index}`}
                  >
                    <CreditCard className="w-4 h-4 mr-2" />
                    Pay
                  </Button>
                )}
              </div>

              {/* Payment Section */}
              {payingSplitIndex === index && !split.isPaid && (
                <div className="mt-4 pt-4 border-t border-slate-200 space-y-3">
                  <div className="flex gap-2">
                    <Button
                      variant={paymentMethod === 'cash' ? 'default' : 'outline'}
                      size="sm"
                      onClick={() => setPaymentMethod('cash')}
                    >
                      <Banknote className="w-4 h-4 mr-2" />
                      Cash
                    </Button>
                    <Button
                      variant={paymentMethod === 'card' ? 'default' : 'outline'}
                      size="sm"
                      onClick={() => setPaymentMethod('card')}
                    >
                      <CreditCard className="w-4 h-4 mr-2" />
                      Card
                    </Button>
                  </div>

                  {paymentMethod === 'cash' && (
                    <div className="flex items-center gap-2">
                      <Label>Cash Received:</Label>
                      <Input
                        type="number"
                        step="0.01"
                        value={cashReceived}
                        onChange={(e) => setCashReceived(e.target.value)}
                        className="w-24"
                        data-testid="cash-received"
                      />
                      {parseFloat(cashReceived) >= split.amount && (
                        <span className="text-green-600 font-medium">
                          Change: £{(parseFloat(cashReceived) - split.amount).toFixed(2)}
                        </span>
                      )}
                    </div>
                  )}

                  <div className="flex gap-2">
                    <Button 
                      variant="outline" 
                      size="sm"
                      onClick={() => setPayingSplitIndex(null)}
                    >
                      Cancel
                    </Button>
                    <Button 
                      size="sm"
                      onClick={() => paySplit(index)}
                      disabled={processing}
                      className="bg-green-600 hover:bg-green-700"
                      data-testid={`confirm-pay-${index}`}
                    >
                      {processing ? (
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      ) : (
                        <Check className="w-4 h-4 mr-2" />
                      )}
                      Confirm £{split.amount.toFixed(2)}
                    </Button>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>

        {/* Add Split Button */}
        {!splits.every(s => s.isPaid) && (
          <Button 
            variant="outline" 
            onClick={addSplit}
            className="w-full mt-4"
            data-testid="add-split"
          >
            <Plus className="w-4 h-4 mr-2" />
            Add Another Person
          </Button>
        )}

        {/* Summary */}
        <div className="bg-slate-100 rounded-lg p-4 mt-4">
          <div className="flex justify-between text-sm mb-2">
            <span>Order Total</span>
            <span>£{order.total.toFixed(2)}</span>
          </div>
          <div className="flex justify-between text-sm mb-2 text-green-600">
            <span>Paid</span>
            <span>£{paidAmount.toFixed(2)}</span>
          </div>
          <div className="flex justify-between font-bold">
            <span>Remaining</span>
            <span className={remainingAmount > 0 ? 'text-orange-500' : 'text-green-600'}>
              £{remainingAmount.toFixed(2)}
            </span>
          </div>
        </div>

        {/* Close Button */}
        <div className="flex justify-end gap-2 mt-4">
          <Button variant="outline" onClick={onClose}>
            {splits.every(s => s.isPaid) ? 'Done' : 'Cancel'}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
